import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { CheckCircle2, Circle, Truck, ShoppingBag } from "lucide-react";
import { api, STORE } from "../lib/api";
import { money } from "../lib/shop";

const DELIVERY_PIPELINE = ["Order Received", "Payment Confirmed", "Being Prepared", "Ready for Dispatch", "Out for Delivery", "Delivered"];
const COLLECTION_PIPELINE = ["Order Received", "Payment Confirmed", "Being Prepared", "Ready for Collection", "Collected"];

export default function TrackOrder() {
    const { orderNumber } = useParams();
    const [params] = useSearchParams();
    const token = params.get("token") || "";
    const [order, setOrder] = useState(null);
    const [error, setError] = useState("");
    const justPlaced = params.get("placed") === "shop";

    useEffect(() => {
        api.get(`/orders/track/${orderNumber}?token=${token}`)
            .then((r) => setOrder(r.data))
            .catch((err) => setError(err.response?.data?.detail || "Order not found"));
    }, [orderNumber, token]);

    if (error)
        return (
            <main className="min-h-[50vh] bg-slate-50 flex items-center justify-center px-4" data-testid="track-error">
                <div className="bg-white rounded-2xl border border-slate-200 p-10 max-w-md text-center">
                    <p className="text-slate-600 mb-4">{error}</p>
                    <p className="text-sm text-slate-400">Check the link in your confirmation email, or call us on {STORE.phones[0].label}.</p>
                </div>
            </main>
        );
    if (!order) return <main className="min-h-[50vh] bg-slate-50" data-testid="track-loading" />;

    const PIPELINE = order.fulfilment === "collection"
        ? (order.payment_method === "shop" ? COLLECTION_PIPELINE.filter((s) => s !== "Payment Confirmed") : COLLECTION_PIPELINE)
        : DELIVERY_PIPELINE;
    const currentIdx = order.status === "Cancelled" || order.status === "Delivery Issue"
        ? -1
        : PIPELINE.indexOf(order.status);

    return (
        <main data-testid="track-page" className="bg-slate-50 min-h-screen py-10 md:py-16">
            <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="bg-white rounded-2xl border border-slate-200 p-6 md:p-10">
                    <p className="text-sm text-slate-500">Order</p>
                    <h1 className="font-heading text-3xl font-extrabold text-[#0033A0]" data-testid="track-order-number">{order.order_number}</h1>
                    <p className="mt-1 text-slate-500 text-sm">
                        Hi {order.customer_name} — {order.fulfilment === "collection" ? "click & collect" : order.fulfilment === "courier_delivery" ? "courier delivery" : "local delivery"} order placed{" "}
                        {new Date(order.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}.
                    </p>

                    {justPlaced && (
                        <p className="mt-4 rounded-xl bg-green-50 border border-green-200 px-4 py-3 text-sm text-green-800 flex items-start gap-2" data-testid="placed-shop-banner">
                            <ShoppingBag className="h-5 w-5 shrink-0" />
                            Order placed! Pay by cash or card when you collect. We've emailed your confirmation — keep your order number handy.
                        </p>
                    )}
                    {order.pickup_time && (
                        <p className="mt-4 rounded-xl bg-[#40E0D0]/15 border border-[#40E0D0]/40 px-4 py-3 text-sm text-[#0033A0] font-bold" data-testid="track-pickup-time">
                            Collection time: {new Date(order.pickup_time).toLocaleString("en-GB", { weekday: "long", day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" })}
                            {order.perishable_hold && (
                                <span className="block text-xs font-normal text-amber-800 mt-1">
                                    Includes fresh/perishable items — held for {order.perishable_hold_hours} hours after your collection time.
                                </span>
                            )}
                        </p>
                    )}

                    {currentIdx >= 0 ? (
                        <ol className="mt-8 space-y-0" data-testid="track-pipeline">
                            {PIPELINE.map((step, i) => {
                                const done = i <= currentIdx;
                                return (
                                    <li key={step} className="flex gap-4" data-testid={`track-step-${step.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                                        <div className="flex flex-col items-center">
                                            {done ? (
                                                <CheckCircle2 className={`h-6 w-6 ${i === currentIdx ? "text-[#0033A0]" : "text-[#40E0D0]"}`} />
                                            ) : (
                                                <Circle className="h-6 w-6 text-slate-200" />
                                            )}
                                            {i < PIPELINE.length - 1 && <span className={`w-0.5 flex-1 min-h-6 ${i < currentIdx ? "bg-[#40E0D0]" : "bg-slate-200"}`} />}
                                        </div>
                                        <p className={`pb-6 font-heading font-bold ${done ? "text-slate-900" : "text-slate-400"}`}>{step}</p>
                                    </li>
                                );
                            })}
                        </ol>
                    ) : (
                        <p className={`mt-8 rounded-xl px-4 py-3 font-bold ${order.status === "Cancelled" ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-800"}`} data-testid="track-special-status">
                            {order.status === "Cancelled"
                                ? "This order has been cancelled. Any payment will be refunded."
                                : "There's a delivery issue with this order — we'll be in touch, or call us on 0118 229 5059."}
                        </p>
                    )}

                    {order.courier && (order.courier.name || order.courier.tracking_url) && (
                        <div className="mt-4 rounded-xl bg-slate-50 border border-slate-200 p-4" data-testid="track-courier">
                            <p className="text-sm font-bold text-slate-900 flex items-center gap-2">
                                <Truck className="h-4 w-4 text-[#0033A0]" /> Courier details
                            </p>
                            {order.courier.name && <p className="text-sm text-slate-600 mt-1">Courier: {order.courier.name}</p>}
                            {order.courier.notes && <p className="text-sm text-slate-600">{order.courier.notes}</p>}
                            {order.courier.tracking_url && (
                                <a href={order.courier.tracking_url} target="_blank" rel="noopener noreferrer"
                                    data-testid="track-courier-link" className="text-sm font-bold text-[#0033A0] hover:underline">
                                    Track with courier
                                </a>
                            )}
                        </div>
                    )}

                    <div className="mt-8 border-t border-slate-100 pt-6">
                        <h2 className="font-heading font-extrabold text-slate-900 mb-3">Items</h2>
                        <ul className="space-y-2 text-sm">
                            {order.items.map((i, idx) => (
                                <li key={idx} className="flex justify-between">
                                    <span className="text-slate-600">{i.sold_by_weight ? `${parseFloat(Number(i.qty).toFixed(2))} kg` : i.qty} × {i.name}{i.variation ? ` (${i.variation})` : ""}</span>
                                    <span className="font-bold">{money(i.line_total)}</span>
                                </li>
                            ))}
                        </ul>
                        <dl className="mt-4 space-y-1 text-sm border-t border-slate-100 pt-4">
                            <div className="flex justify-between"><dt className="text-slate-500">Subtotal</dt><dd>{money(order.subtotal)}</dd></div>
                            {order.discount_amount > 0 && (
                                <div className="flex justify-between text-green-700"><dt>Discount</dt><dd>-{money(order.discount_amount)}</dd></div>
                            )}
                            <div className="flex justify-between"><dt className="text-slate-500">Delivery</dt><dd>{order.delivery_fee > 0 ? money(order.delivery_fee) : "Free"}</dd></div>
                            <div className="flex justify-between font-heading font-extrabold text-base"><dt>Total</dt><dd className="text-[#0033A0]">{money(order.total)}</dd></div>
                        </dl>
                        <p className="mt-3 text-xs text-slate-400">
                            Payment: {order.payment_status === "pay_at_shop" ? "Pay at the shop on collection" : order.payment_status}
                        </p>
                    </div>

                    <p className="mt-8 text-sm text-slate-500 text-center">
                        Questions about your order? Call us on{" "}
                        <a href={`tel:${STORE.phones[0].tel}`} className="font-bold text-[#0033A0] hover:underline">{STORE.phones[0].label}</a>
                    </p>
                </div>
                <p className="mt-6 text-center">
                    <Link to="/products" className="text-[#0033A0] font-bold hover:underline text-sm" data-testid="track-shop-link">
                        Continue Shopping
                    </Link>
                </p>
            </div>
        </main>
    );
}
